import { formatCurrency } from '@/utils/format-currency';
import { formatFeePercent } from '@/utils/payment-simulator';
import { PaymentPricing } from '@/types/payment-account';

/**
 * Helpers de texto compartilhados entre as telas de `app/content/payments/`.
 * Nenhuma tela escreve taxa, prazo, limite ou bandeira direto — tudo passa
 * por aqui, a partir do `PaymentPricing` que vem de `usePaymentPricing()`.
 */

const CARD_BRAND_LABELS: Record<string, string> = {
  visa: 'Visa',
  mastercard: 'Mastercard',
  elo: 'Elo',
  amex: 'Amex',
  hipercard: 'Hipercard',
};

/** `['visa', 'mastercard']` → `"Visa e Mastercard"`. */
export function formatCardBrands(brands: string[]): string {
  const labels = brands.map((brand) => CARD_BRAND_LABELS[brand] ?? brand);
  if (labels.length === 0) return '';
  if (labels.length === 1) return labels[0];
  return `${labels.slice(0, -1).join(', ')} e ${labels[labels.length - 1]}`;
}

/** "1,19%" · "3,99% + R$ 0,39" · "R$ 3,45". */
export function formatFeeSummary(fee: { percent: number; fixed: number }): string {
  const hasPercent = fee.percent > 0;
  const hasFixed = fee.fixed > 0;
  if (hasPercent && hasFixed)
    return `${formatFeePercent(fee.percent)} + ${formatCurrency(fee.fixed)}`;
  if (hasPercent) return formatFeePercent(fee.percent);
  return formatCurrency(fee.fixed);
}

/** "Pix 1,19% · cartão 3,99% + R$ 0,39 · boleto R$ 3,45" — a linha usada no comparativo e no artigo de taxas. */
export function formatFeeLine(pricing: PaymentPricing): string {
  const pix = formatFeeSummary({ percent: pricing.fee.pixPercent, fixed: 0 });
  const card = formatFeeSummary({ percent: pricing.fee.cardPercent, fixed: pricing.fee.cardFixed });
  const boleto = formatFeeSummary({ percent: 0, fixed: pricing.fee.boletoFixed });
  return `Pix ${pix} · cartão ${card} · boleto ${boleto}`;
}

/** "Pix/boleto em 2 dias úteis · cartão em 30 dias". */
export function formatSettlementLine(pricing: PaymentPricing): string {
  return `Pix/boleto em ${pricing.settlement.pixBusinessDays} dias úteis · cartão em ${pricing.settlement.cardDays} dias`;
}

/** "de R$ 5,00 a R$ 49.999,99" — faixa aceita do boleto. */
export function formatBoletoRange(pricing: PaymentPricing): string {
  return `de ${formatCurrency(pricing.boletoMin)} a ${formatCurrency(pricing.boletoMax)}`;
}

/** Faixa do Pix por cobrança: "de R$ 0,50 a R$ 15.000,00 por cobrança". */
export function formatPixLimit(pricing: PaymentPricing): string {
  return pricing.pixMax != null
    ? `de ${formatCurrency(pricing.pixMin)} a ${formatCurrency(pricing.pixMax)} por cobrança`
    : `a partir de ${formatCurrency(pricing.pixMin)} por cobrança`;
}
